<template>
  <AppHeaderLayout>
    <div class="page">
      <section class="tab-card">
        <div class="tabs">
          <button
            type="button"
            class="tab"
            :class="{ active: activeTab === 'receive' }"
            @click="activeTab = 'receive'"
          >
            接收資料
          </button>
          <button
            type="button"
            class="tab"
            :class="{ active: activeTab === 'send' }"
            @click="activeTab = 'send'"
          >
            傳送資料
          </button>
        </div>

        <div v-if="activeTab === 'receive'" class="tab-body">
          <div class="receive-filter">
            <label class="form-field">
              <span>Mobile</span>
              <select v-model="receiveSelectedMobileId">
                <option disabled value="">請選擇 Mobile</option>
                <option v-for="mobile in mobiles" :key="`receive-${mobile.id}`" :value="mobile.mobileId">
                  {{ mobile.mobileId }}
                </option>
              </select>
            </label>
            <label class="form-field">
              <span>起始日期</span>
              <input v-model="receiveStartDate" type="date" />
            </label>
            <label class="form-field">
              <span>結束日期</span>
              <input v-model="receiveEndDate" type="date" />
            </label>
            <button class="btn primary query-btn" type="button" @click="queryReceive">查詢</button>
          </div>

          <div class="table-card">
            <table>
              <thead>
                <tr>
                  <th>SIN</th>
                  <th>MIN</th>
                  <th>From</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in pagedReceiveList"
                  :key="item.messageId || item.returnMessageId"
                  @click="openDetail(item)"
                  class="clickable-row"
                >
                  <td>{{ item.sin }}</td>
                  <td>{{ item.min }}</td>
                  <td>{{ item.from }}</td>
                  <td>{{ item.received }}</td>
                </tr>
                <tr v-if="isLoadingReceive">
                  <td colspan="4" class="empty">資料查詢中...</td>
                </tr>
                <tr v-else-if="receiveErrorMessage">
                  <td colspan="4" class="empty">{{ receiveErrorMessage }}</td>
                </tr>
                <tr v-else-if="receiveList.length === 0">
                  <td colspan="4" class="empty">目前沒有接收資料</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="table-footer">
            <div class="page-size">
              <span>每頁顯示</span>
              <select v-model.number="receivePageSize">
                <option v-for="size in pageSizes" :key="`receive-${size}`" :value="size">{{ size }}</option>
              </select>
              <span>筆</span>
              <span>共 {{ receiveTotalCount }} 筆</span>
            </div>
            <div class="pager">
              <button type="button" :disabled="!canPrevReceive" @click="prevReceivePage" aria-label="上一頁">
                &lt;
              </button>
              <span>{{ receiveCurrentPage }} / {{ receiveTotalPages }}</span>
              <button type="button" :disabled="!canNextReceive" @click="nextReceivePage" aria-label="下一頁">
                &gt;
              </button>
            </div>
          </div>
        </div>

        <div v-else class="tab-body">
          <div class="send-mobile-picker">
            <label class="form-field">
              <span>Mobile</span>
              <select v-model="selectedMobileId">
                <option disabled value="">請選擇 Mobile</option>
                <option v-for="mobile in mobiles" :key="mobile.id" :value="mobile.mobileId">
                  {{ mobile.mobileId }}
                </option>
              </select>
            </label>
          </div>

          <section class="send-compose-card">
            <div v-if="canUseEncoded && !isCustomerMember" class="send-tabs">
              <button
                type="button"
                class="send-tab"
                :class="{ active: sendTab === 'encoded' }"
                @click="sendTab = 'encoded'"
              >
                Command
              </button>
              <button
                type="button"
                class="send-tab"
                :class="{ active: sendTab === 'raw' }"
                @click="sendTab = 'raw'"
              >
                Message
              </button>
            </div>

            <div v-if="canUseEncoded && (isCustomerMember || sendTab === 'encoded')" class="send-panel">
              <div class="form-grid">
                <label class="form-field">
                  <span>Command</span>
                  <select v-model="encoded.commandKey" :disabled="isLoadingEncodedDefinitions">
                    <option disabled value="">請選擇 Command</option>
                    <option v-for="item in commandOptions" :key="item.value" :value="item.value">
                      {{ item.label }}
                    </option>
                  </select>
                </label>
              </div>
              <div v-if="encodedDefinitionError" class="inline-error">{{ encodedDefinitionError }}</div>
              <div v-else-if="isLoadingEncodedDefinitions" class="placeholder">載入 Encoded 定義中...</div>
              <div v-else-if="selectedEncodedMinDef" class="encoded-layout">
                <div class="encoded-tree">
                  <div class="encoded-tree-title">Fields</div>
                  <button
                    v-for="node in encodedTreeNodes"
                    :key="node.key"
                    type="button"
                    class="encoded-field-item"
                    :class="{ active: encoded.selectedNodeKey === node.key, nested: node.depth > 0 }"
                    :style="{ paddingLeft: `${12 + node.depth * 20}px` }"
                    @click="encoded.selectedNodeKey = node.key"
                  >
                    <span>{{ node.label }}</span>
                    <small>{{ node.summary }}</small>
                  </button>
                </div>
                <div class="encoded-editor">
                  <div class="form-field">
                    <span>Command Description</span>
                    <div class="description-box">{{ selectedEncodedMinDef.description || "-" }}</div>
                  </div>

                  <template v-if="selectedEncodedNode && selectedEncodedNode.nodeType === 'field'">
                    <template v-if="isDynamicEncodedField(selectedEncodedNode.field)">
                      <label class="form-field">
                        <span>Field Type</span>
                        <select
                          :value="getEncodedNodeFieldType(selectedEncodedNode)"
                          :key="`${selectedEncodedNode.key}-field-type`"
                          :disabled="selectedEncodedNode.field.readOnly === true"
                          @change="setEncodedNodeFieldType(selectedEncodedNode, $event.target.value)"
                        >
                          <option
                            v-for="option in getDynamicFieldTypeOptions(selectedEncodedNode.field)"
                            :key="`${selectedEncodedNode.field.name}-field-type-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </label>
                      <label class="form-field">
                        <span>{{ getEncodedNodeValueLabel(selectedEncodedNode) }}</span>
                        <select
                          v-if="shouldRenderEncodedNodeValueAsSelect(selectedEncodedNode)"
                          :key="`${selectedEncodedNode.key}-value-${getEncodedNodeFieldType(selectedEncodedNode)}`"
                          :value="getEncodedNodeValue(selectedEncodedNode)"
                          :disabled="selectedEncodedNode.field.readOnly === true"
                          @change="setEncodedNodeValue(selectedEncodedNode, $event.target.value)"
                        >
                          <option
                            v-for="option in getEncodedNodeValueOptions(selectedEncodedNode)"
                            :key="`${selectedEncodedNode.field.name}-value-${option.value}`"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                        <textarea
                          v-else-if="isEncodedNodeTextarea(selectedEncodedNode)"
                          :key="`${selectedEncodedNode.key}-value-${getEncodedNodeFieldType(selectedEncodedNode)}`"
                          :value="getEncodedNodeValue(selectedEncodedNode)"
                          rows="6"
                          :readonly="selectedEncodedNode.field.readOnly === true"
                          :placeholder="selectedEncodedNode.field.readOnly === true ? '' : '請輸入'"
                          @input="setEncodedNodeValue(selectedEncodedNode, $event.target.value)"
                        ></textarea>
                        <input
                          v-else-if="isEncodedNodeNumeric(selectedEncodedNode)"
                          :key="`${selectedEncodedNode.key}-value-${getEncodedNodeFieldType(selectedEncodedNode)}`"
                          :value="getEncodedNodeValue(selectedEncodedNode)"
                          type="text"
                          inputmode="numeric"
                          pattern="[0-9-]*"
                          :readonly="selectedEncodedNode.field.readOnly === true"
                          :placeholder="selectedEncodedNode.field.readOnly === true ? '' : '請輸入'"
                          @input="handleEncodedNumericInput(selectedEncodedNode, $event)"
                        />
                        <input
                          v-else
                          :key="`${selectedEncodedNode.key}-value-${getEncodedNodeFieldType(selectedEncodedNode)}`"
                          :value="getEncodedNodeValue(selectedEncodedNode)"
                          type="text"
                          :readonly="selectedEncodedNode.field.readOnly === true"
                          :placeholder="selectedEncodedNode.field.readOnly === true ? '' : '請輸入'"
                          @input="setEncodedNodeValue(selectedEncodedNode, $event.target.value)"
                        />
                      </label>
                    </template>
                    <label v-else class="form-field">
                      <span>{{ selectedEncodedNode.field.label || selectedEncodedNode.field.name }}</span>
                      <select
                        v-if="shouldRenderEncodedSelect(selectedEncodedNode.field)"
                        :key="`${selectedEncodedNode.key}-value`"
                        :value="getEncodedNodeValue(selectedEncodedNode)"
                        :disabled="selectedEncodedNode.field.readOnly === true"
                        @change="setEncodedNodeValue(selectedEncodedNode, $event.target.value)"
                      >
                        <option
                          v-for="option in getEncodedFieldOptions(selectedEncodedNode.field)"
                          :key="`${selectedEncodedNode.field.name}-${option.value}`"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <input
                        v-else-if="selectedEncodedNode.field.type === 'uint'"
                        :key="`${selectedEncodedNode.key}-value`"
                        :value="getEncodedNodeValue(selectedEncodedNode)"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        :readonly="selectedEncodedNode.field.readOnly === true"
                        :placeholder="selectedEncodedNode.field.readOnly === true ? '' : '請輸入'"
                        @input="handleEncodedNumericInput(selectedEncodedNode, $event)"
                      />
                      <input
                        v-else
                        :key="`${selectedEncodedNode.key}-value`"
                        :value="getEncodedNodeValue(selectedEncodedNode)"
                        type="text"
                        :readonly="selectedEncodedNode.field.readOnly === true"
                        :placeholder="selectedEncodedNode.field.readOnly === true ? '' : '請輸入'"
                        @input="setEncodedNodeValue(selectedEncodedNode, $event.target.value)"
                      />
                    </label>
                    <div class="form-field">
                      <span>Field Description</span>
                      <div class="description-box">{{ selectedEncodedNode.field.description || "-" }}</div>
                    </div>
                  </template>

                  <template v-else-if="selectedEncodedNode && selectedEncodedNode.nodeType === 'array'">
                    <label class="form-field">
                      <span>Array Elements</span>
                      <input
                        :value="getEncodedArrayCount(selectedEncodedNode)"
                        type="number"
                        step="1"
                        :min="selectedEncodedNode.field.minElements ?? 0"
                        :max="selectedEncodedNode.field.maxElements ?? 255"
                        :disabled="selectedEncodedNode.field.readOnly === true"
                        :readonly="selectedEncodedNode.field.readOnly === true"
                        @input="setEncodedArrayCount(selectedEncodedNode, $event.target.value)"
                      />
                    </label>
                    <div class="form-field">
                      <span>Field Description</span>
                      <div class="description-box">{{ selectedEncodedNode.field.description || "-" }}</div>
                    </div>
                  </template>

                  <template v-else-if="selectedEncodedNode && selectedEncodedNode.nodeType === 'array-element'">
                    <div class="form-field">
                      <span>Element</span>
                      <div class="description-box">請在左側選擇此 element 底下的欄位進行編輯。</div>
                    </div>
                  </template>
                </div>
              </div>
              <div class="send-actions">
                <button class="btn primary" type="button" @click="sendEncoded">傳送</button>
              </div>
            </div>

            <div v-else-if="!isCustomerMember && (!canUseEncoded || sendTab === 'raw')" class="send-panel">
              <div class="raw-toolbar">
                <button class="btn primary" type="button" @click="triggerLoadAsciiTxt">Load...</button>
                <button class="btn primary" type="button" @click="saveRawAsciiToTxt">Save...</button>
                <input
                  ref="rawAsciiFileInput"
                  type="file"
                  accept=".txt,text/plain"
                  style="display: none"
                  @change="handleLoadAsciiTxt"
                />
              </div>
              <div class="form-grid">
                <label class="form-field">
                  <span>SIN</span>
                  <input
                    v-model="raw.sin"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    placeholder="請輸入"
                    @input="clampRawHeaderValue('sin')"
                  />
                </label>
                <label class="form-field">
                  <span>MIN</span>
                  <input
                    v-model="raw.min"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    placeholder="請輸入"
                    @input="clampRawHeaderValue('min')"
                  />
                </label>
              </div>
              <label class="form-field">
                <span>Data</span>
                <div class="data-box">
                  <div class="data-tabs">
                    <button
                      type="button"
                      class="data-tab"
                      :class="{ active: raw.mode === 'hex' }"
                      @click="switchRawMode('hex')"
                    >
                      HEX
                    </button>
                    <button
                      type="button"
                      class="data-tab"
                      :class="{ active: raw.mode === 'ascii' }"
                      @click="switchRawMode('ascii')"
                    >
                      ASCII
                    </button>
                  </div>
                  <textarea
                    v-model="raw.data"
                    rows="4"
                    placeholder="請輸入訊息內容"
                  ></textarea>
                </div>
              </label>
              <div class="send-actions">
                <button class="btn primary" type="button" @click="sendRaw">傳送</button>
              </div>
            </div>
          </section>

          <section class="control-card">
            <label class="form-field">
                  <span>起始日期</span>
              <input v-model="sendStartDate" type="date" />
            </label>
            <label class="form-field">
                  <span>結束日期</span>
              <input v-model="sendEndDate" type="date" />
            </label>
            <button class="btn primary query-btn" type="button" @click="querySend">查詢</button>

            <div class="control-card-section">
              <div class="table-card send-history">
                <table>
                  <thead>
                    <tr>
                      <th>SIN</th>
                      <th>MIN</th>
                      <th>To</th>
                      <th>Send</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in pagedSendList"
                      :key="item.messageId"
                      @click="openSendDetail(item)"
                      class="clickable-row"
                    >
                      <td>{{ getSinDisplay(item.sin) }}</td>
                      <td>{{ getMinDisplay(item.sin, item.min, item.payloadFields) }}</td>
                      <td>{{ item.to }}</td>
                      <td>{{ item.sent }}</td>
                      <td>{{ item.state }}</td>
                    </tr>
                    <tr v-if="isLoadingSend">
                      <td colspan="5" class="empty">資料查詢中...</td>
                    </tr>
                    <tr v-else-if="sendErrorMessage">
                      <td colspan="5" class="empty">{{ sendErrorMessage }}</td>
                    </tr>
                    <tr v-else-if="sendList.length === 0">
                      <td colspan="5" class="empty">尚未有傳送紀錄</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="table-footer">
                <div class="page-size">
                  <span>每頁顯示</span>
                  <select v-model.number="sendPageSize">
                    <option v-for="size in pageSizes" :key="`send-${size}`" :value="size">{{ size }}</option>
                  </select>
                  <span>筆</span>
                  <span>共 {{ sendTotalCount }} 筆</span>
                </div>
                <div class="pager">
                  <button type="button" :disabled="!canPrevSend" @click="prevSendPage" aria-label="上一頁">
                    &lt;
                  </button>
                  <span>{{ sendCurrentPage }} / {{ sendTotalPages }}</span>
                  <button type="button" :disabled="!canNextSend" @click="nextSendPage" aria-label="下一頁">
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>

    <div v-if="showDetail" class="modal-backdrop" @click.self="closeDetail">
      <div class="modal">
        <div class="modal-header">
          <h2>訊息明細</h2>
          <button class="modal-close" type="button" @click="closeDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-item"><span>SIN:</span>{{ detailItem.sin }}</div>
            <div class="detail-item"><span>MIN:</span>{{ detailItem.min }}</div>
            <div class="detail-item"><span>From:</span>{{ detailItem.from }}</div>
            <div class="detail-item"><span>Received:</span>{{ detailItem.received }}</div>
            <div class="detail-item"><span>Message ID:</span>{{ detailItem.messageId }}</div>
            <div class="detail-item"><span>Transport:</span>{{ detailItem.transport }}</div>
            <div class="detail-item"><span>Region:</span>{{ detailItem.region }}</div>
            <div class="detail-item"><span>Message Length:</span>{{ detailItem.messageLength }}</div>
            <div class="detail-item detail-item-left"><span>Data Length:</span>{{ detailItem.dataLength }}</div>
            <div class="detail-item detail-item-left"><span>Data(hex):</span>{{ detailItem.dateHex }}</div>
            <div class="detail-item detail-item-left"><span>Data(ASCII):</span>{{ detailItem.dataAscii }}</div>
            <div class="detail-item detail-item-full">
              <span>Messenger Decode:</span>
              <div class="messenger-decode">
                <template v-if="detailItem.messengerDecodeLines && detailItem.messengerDecodeLines.length">
                  <div v-for="(line, index) in detailItem.messengerDecodeLines" :key="`decode-${index}`">{{ line }}</div>
                </template>
                <template v-else>-</template>
              </div>
            </div>
            <div v-if="detailItem.hasLatLongDecode" class="detail-item detail-item-full">
              <div><span>Latitude:</span>{{ detailItem.latitudeDisplay }}</div>
              <div><span>Longitude:</span>{{ detailItem.longitudeDisplay }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn primary" type="button" @click="closeDetail">關閉</button>
        </div>
      </div>
    </div>

    <div v-if="showSendDetail" class="modal-backdrop" @click.self="closeSendDetail">
      <div class="modal">
        <div class="modal-header">
          <h2>訊息明細</h2>
          <button class="modal-close" type="button" @click="closeSendDetail">×</button>
        </div>
        <div class="modal-body">
          <div class="detail-grid">
            <div class="detail-item"><span>SIN:</span>{{ getSinDisplay(sendDetailItem.sin) }}</div>
            <div class="detail-item"><span>MIN:</span>{{ getMinDisplay(sendDetailItem.sin, sendDetailItem.min, sendDetailItem.payloadFields) }}</div>
            <div class="detail-item"><span>To:</span>{{ sendDetailItem.to }}</div>
            <div class="detail-item"><span>Send:</span>{{ sendDetailItem.sent }}</div>
            <div class="detail-item"><span>State:</span>{{ sendDetailItem.state }}</div>
            <div class="detail-item"><span>Acknowledged:</span>{{ sendDetailItem.acknowledged }}</div>
            <div class="detail-item"><span>Transport:</span>{{ sendDetailItem.transport }}</div>
            <div class="detail-item"><span>Message ID:</span>{{ sendDetailItem.messageId }}</div>
            <div class="detail-item"><span>Message Length:</span>{{ sendDetailItem.messageLength }}</div>
            <div class="detail-item"><span>Data Length:</span>{{ sendDetailItem.dataLength }}</div>
            <div class="detail-item detail-item-left"><span>Data(hex):</span>{{ sendDetailItem.dataHex }}</div>
            <div class="detail-item detail-item-left"><span>Data(ASCII):</span>{{ sendDetailItem.dataAscii }}</div>
            <div class="detail-item detail-item-full">
              <span>Messenger Decode:</span>
              <div class="messenger-decode">
                <template v-if="sendDetailItem.messengerDecodeLines && sendDetailItem.messengerDecodeLines.length">
                  <div v-for="(line, index) in sendDetailItem.messengerDecodeLines" :key="`send-decode-${index}`">{{ line }}</div>
                </template>
                <template v-else>-</template>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn primary" type="button" @click="closeSendDetail">關閉</button>
        </div>
      </div>
    </div>

  </AppHeaderLayout>
</template>

<script>
import AppHeaderLayout from "@/components/AppHeaderLayout.vue";
import {
  GET_ENCODED_DEFINITIONS,
  GET_FORWARD_STATUS_BY_PAGE,
  GET_MEMBER_MOBILE,
  GET_RETURN_MESSAGE_BY_PAGE,
  SUBMIT_ENCODED_MESSAGES,
  SUBMIT_RAW_MESSAGES
} from "@/utils/requestUrl";
import { getToken, getUserId } from "@/utils/sessionStoraggeUtil";

export default {
  name: "DataProcessView",
  components: {
    AppHeaderLayout
  },
  data() {
    return {
      mobiles: [],
      selectedMobileId: "",
      receiveSelectedMobileId: "",
      receiveStartDate: "",
      receiveEndDate: "",
      sendStartDate: "",
      sendEndDate: "",
      isConnected: false,
      activeTab: "receive",
      sendTab: "encoded",
      receiveList: [],
      receiveSnapshot: "",
      receiveTotalCount: 0,
      receiveTotalPagesFromApi: 0,
      sendList: [],
      sendSnapshot: "",
      sendTotalCount: 0,
      sendTotalPagesFromApi: 0,
      pageSizes: [10, 50, 100],
      receivePageSize: 10,
      receiveCurrentPage: 1,
      sendPageSize: 10,
      sendCurrentPage: 1,
      pollTimer: null,
      isLoadingMobiles: false,
      mobileLoadError: "",
      isLoadingReceive: false,
      receiveErrorMessage: "",
      isLoadingSend: false,
      sendErrorMessage: "",
      showDetail: false,
      detailItem: {},
      showSendDetail: false,
      sendDetailItem: {},
      encodedDefinitions: [],
      isLoadingEncodedDefinitions: false,
      encodedDefinitionError: "",
      encoded: {
        commandKey: "",
        sin: "",
        min: "",
        selectedNodeKey: "",
        fieldValues: {}
      },
      raw: {
        sin: "",
        min: "",
        mode: "hex",
        data: ""
      }
    };
  },
  computed: {
    currentMemberType() {
      const userInfo = this.$store.getters.getUserInfo || {};
      return String(userInfo.memberType || userInfo.MemberType || "").trim().toUpperCase();
    },
    isCustomerMember() {
      return this.currentMemberType === "C";
    },
    canUseEncoded() {
      return true;
    },
    canConnect() {
      return !this.isConnected && this.selectedMobileId;
    },
    receiveTotalPages() {
      return Math.max(1, this.receiveTotalPagesFromApi || 1);
    },
    sendTotalPages() {
      return Math.max(1, this.sendTotalPagesFromApi || 1);
    },
    pagedReceiveList() {
      return this.receiveList;
    },
    pagedSendList() {
      return this.sendList;
    },
    canPrevReceive() {
      return this.receiveCurrentPage > 1;
    },
    canNextReceive() {
      return this.receiveCurrentPage < this.receiveTotalPages;
    },
    canPrevSend() {
      return this.sendCurrentPage > 1;
    },
    canNextSend() {
      return this.sendCurrentPage < this.sendTotalPages;
    },
    encodedSinOptions() {
      return this.encodedDefinitions || [];
    },
    commandOptions() {
      return (this.encodedDefinitions || []).flatMap((sinDef, sinIndex) =>
        (sinDef.mins || [])
          .map((minDef, minIndex) => ({
            value: `${sinIndex}:${minIndex}`,
            sin: String(sinDef.sin),
            min: String(minDef.min),
            sinIndex,
            minIndex,
            label: `${sinDef.sinName || sinDef.sinLabel || sinDef.sin} - ${minDef.minName || minDef.minLabel || minDef.min}`
          }))
          .filter((item) => this.isEncodedCommandAllowed((sinDef.mins || [])[item.minIndex]))
      );
    },
    selectedEncodedCommandOption() {
      return this.commandOptions.find((item) => item.value === this.encoded.commandKey) || null;
    },
    selectedEncodedSinDef() {
      const sinIndex = this.selectedEncodedCommandOption?.sinIndex;
      if (!Number.isInteger(sinIndex)) {
        return null;
      }
      return this.encodedDefinitions[sinIndex] || null;
    },
    encodedMinOptions() {
      return this.selectedEncodedSinDef?.mins || [];
    },
    selectedEncodedMinDef() {
      const minIndex = this.selectedEncodedCommandOption?.minIndex;
      if (!Number.isInteger(minIndex)) {
        return null;
      }
      return this.encodedMinOptions[minIndex] || null;
    },
    encodedFieldDefs() {
      return this.selectedEncodedMinDef?.fields || [];
    },
    encodedTreeNodes() {
      return this.buildEncodedTreeNodes();
    },
    selectedEncodedNode() {
      return this.encodedTreeNodes.find((item) => item.key === this.encoded.selectedNodeKey) || null;
    }
  },
  watch: {
    currentMemberType: {
      immediate: true,
      handler(memberType) {
        if (memberType === "C") {
          this.sendTab = "encoded";
          return;
        }
        this.sendTab = this.canUseEncoded ? "encoded" : "raw";
      }
    },
    canUseEncoded: {
      immediate: true,
      handler(canUseEncoded) {
        if (this.isCustomerMember) {
          this.sendTab = "encoded";
          return;
        }
        this.sendTab = canUseEncoded ? "encoded" : "raw";
      }
    },
    sendTab: {
      immediate: true,
      handler(nextTab) {
        if (nextTab === "encoded" && this.canUseEncoded && this.encodedDefinitions.length === 0) {
          this.fetchEncodedDefinitions();
        }
      }
    },
    "encoded.commandKey"(value) {
      if (!value) {
        this.encoded.sin = "";
        this.encoded.min = "";
        this.encoded.selectedNodeKey = "";
        this.encoded.fieldValues = {};
        return;
      }
      const option = this.commandOptions.find((item) => item.value === value);
      this.encoded.sin = option?.sin || "";
      this.encoded.min = option?.min || "";
      this.initializeEncodedFieldValues();
    },
    activeTab() {
      this.resetPolling();
    },
    isConnected() {
      this.resetPolling();
    },
    async receivePageSize() {
      this.receiveCurrentPage = 1;
      if (this.receiveSnapshot) {
        await this.fetchReceiveListByPage(false);
      }
    },
    async sendPageSize() {
      this.sendCurrentPage = 1;
      if (this.sendSnapshot) {
        await this.fetchSendListByPage(false);
      }
    },
    receiveList() {
      this.ensureReceivePageInRange();
    },
    sendList() {
      this.ensureSendPageInRange();
    }
  },
  async created() {
    await this.fetchMemberMobiles();
    if (this.canUseEncoded) {
      await this.fetchEncodedDefinitions();
    }
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    isEncodedCommandAllowed(minDef) {
      const rawAllowedMemberTypes = minDef?.allowedMemberTypes ?? minDef?.AllowedMemberTypes;
      const allowedMemberTypes = Array.isArray(rawAllowedMemberTypes) && rawAllowedMemberTypes.length > 0
        ? rawAllowedMemberTypes.map((item) => String(item || "").trim().toUpperCase()).filter((item) => item)
        : ["A"];

      return allowedMemberTypes.includes(this.currentMemberType);
    },
    getApiBase() {
      if (window._env_ && window._env_.VUE_APP_REQUEST_DOMAIN) {
        return window._env_.VUE_APP_REQUEST_DOMAIN;
      }
      return "";
    },
    buildHeaders() {
      const headers = {
        "Content-Type": "application/json"
      };
      const token = getToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      return headers;
    },
    getUserMemberId() {
      const userInfo = this.$store.getters.getUserInfo || {};
      return userInfo.memberId || userInfo.MemberId || getUserId() || "";
    },
    async fetchEncodedDefinitions() {
      this.isLoadingEncodedDefinitions = true;
      this.encodedDefinitionError = "";

      try {
        const response = await fetch(this.getApiBase() + GET_ENCODED_DEFINITIONS, {
          method: "POST",
          headers: this.buildHeaders()
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        const statusText = String(payload?.status ?? payload?.Status ?? "").toLowerCase();
        if (!response.ok || statusText === "failure") {
          throw new Error((payload && payload.message) || `取得 Encoded 定義失敗 (${response.status})`);
        }

        const data = payload?.data || payload?.Data || {};
        this.encodedDefinitions = data.definitions || data.Definitions || [];
      } catch (error) {
        this.encodedDefinitions = [];
        this.encodedDefinitionError = error.message || "取得 Encoded 定義失敗";
      } finally {
        this.isLoadingEncodedDefinitions = false;
      }
    },
    initializeEncodedFieldValues() {
      const values = {};
      const fields = this.encodedFieldDefs || [];

      fields.forEach((field) => {
        values[field.name] = this.createDefaultEncodedFieldValue(field);
      });

      this.encoded.fieldValues = values;
      this.encoded.selectedNodeKey = fields.length > 0 ? fields[0].name : "";
    },
    createDefaultEncodedFieldValue(field) {
      if (field.type === "array") {
        const minElements = Number(field.minElements ?? 0);
        const initialCount = Number.isFinite(minElements) ? Math.max(0, Math.round(minElements)) : 0;
        const elements = [];
        for (let index = 0; index < initialCount; index += 1) {
          elements.push(this.createDefaultEncodedElement(field.elementFields));
        }

        return {
          elements
        };
      }

      if (this.isDynamicEncodedField(field)) {
        const fieldType = this.getDefaultDynamicFieldType(field);
        return {
          fieldType,
          value: this.getDefaultDynamicFieldValue(field, fieldType)
        };
      }

      if (field.type === "uint") {
        if (field.readOnly === true) {
          return Number(field.defaultValue ?? field.minValue ?? 0);
        }
        return "";
      }

      return String(field.defaultValue ?? "");
    },
    createDefaultEncodedElement(fieldDefs) {
      const element = {};
      (fieldDefs || []).forEach((field) => {
        element[field.name] = this.createDefaultEncodedFieldValue(field);
      });
      return element;
    },
    buildEncodedTreeNodes() {
      const nodes = [];
      (this.encodedFieldDefs || []).forEach((field) => {
        this.appendEncodedFieldNodes(nodes, field, [field.name], 0, field.name);
      });

      return nodes;
    },
    appendEncodedFieldNodes(nodes, field, valuePath, depth, keyPrefix) {
      const value = this.getEncodedValueByPath(valuePath);
      nodes.push({
        key: keyPrefix,
        nodeType: field.type === "array" ? "array" : "field",
        label: field.name,
        summary: this.formatEncodedValue(field, value),
        depth,
        field,
        valuePath
      });

      if (field.type !== "array") {
        return;
      }

      const elements = value?.elements || [];
      elements.forEach((element, index) => {
        const elementPath = [...valuePath, "elements", index];
        const elementKey = `${keyPrefix}[${index}]`;
        nodes.push({
          key: elementKey,
          nodeType: "array-element",
          label: `[${index}]`,
          summary: `${(field.elementFields || []).length} field(s)`,
          depth: depth + 1,
          field,
          index,
          valuePath: elementPath
        });

        (field.elementFields || []).forEach((childField) => {
          this.appendEncodedFieldNodes(
            nodes,
            childField,
            [...elementPath, childField.name],
            depth + 2,
            `${elementKey}.${childField.name}`
          );
        });
      });
    },
    getEncodedValueByPath(path) {
      return (path || []).reduce((current, segment) => {
        if (current === null || current === undefined) {
          return undefined;
        }
        return current[segment];
      }, this.encoded.fieldValues);
    },
    getEncodedParentByPath(path) {
      if (!Array.isArray(path) || path.length === 0) {
        return {
          parent: null,
          key: null
        };
      }

      const parentPath = path.slice(0, -1);
      return {
        parent: parentPath.length > 0 ? this.getEncodedValueByPath(parentPath) : this.encoded.fieldValues,
        key: path[path.length - 1]
      };
    },
    formatEncodedValue(field, value) {
      if (field.type === "array") {
        return `${(value?.elements || []).length} element(s)`;
      }

      if (this.isDynamicEncodedField(field)) {
        if (!value) {
          return "-";
        }
        const fieldType = value.fieldType || this.getDefaultDynamicFieldType(field);
        const displayValue = value.value === null || value.value === undefined || value.value === "" ? "-" : String(value.value);
        return `${fieldType}: ${displayValue}`;
      }

      if (value === null || value === undefined || value === "") {
        return "-";
      }

      if (this.shouldRenderEncodedSelect(field)) {
        const option = this.getEncodedFieldOptions(field).find((item) => String(item.value) === String(value));
        return option ? option.label : String(value);
      }

      return String(value);
    },
    isDynamicEncodedField(field) {
      return Boolean(field?.allowFieldTypeSelection) || field?.type === "dynamic";
    },
    getDynamicFieldTypeOptions(field) {
      if (Array.isArray(field?.fieldTypeOptions) && field.fieldTypeOptions.length > 0) {
        return field.fieldTypeOptions;
      }
      return [];
    },
    getDefaultDynamicFieldType(field) {
      const options = this.getDynamicFieldTypeOptions(field);
      return field?.defaultFieldType || options[0]?.value || "enum";
    },
    getDefaultDynamicFieldValue(field, fieldType) {
      switch (fieldType) {
        case "boolean":
          return ["True", "False"].includes(String(field?.defaultValue)) ? String(field.defaultValue) : "False";
        case "unsignedint":
        case "signedint":
          return field?.readOnly === true ? String(field?.defaultValue ?? "0") : "";
        case "string":
          return "";
        case "data":
          return String(field?.defaultValue ?? "");
        case "enum":
        default:
          return String(field?.defaultValue ?? field?.options?.[0]?.value ?? "0");
      }
    },
    shouldRenderEncodedSelect(field) {
      if (!field) {
        return false;
      }

      if (field.type === "enum") {
        return true;
      }

      return field.control === "select";
    },
    getEncodedFieldOptions(field) {
      if (!field) {
        return [];
      }

      if (Array.isArray(field.options) && field.options.length > 0) {
        return field.options;
      }

      if (field.control === "select" && field.type === "uint") {
        const min = Number(field.minValue ?? 0);
        const max = Number(field.maxValue ?? min);
        if (Number.isFinite(min) && Number.isFinite(max) && max >= min && max - min <= 255) {
          return Array.from({ length: max - min + 1 }, (_, index) => {
            const value = String(min + index);
            return {
              value,
              label: value
            };
          });
        }
      }

      return [];
    },
    getEncodedNodeValue(node) {
      const state = this.getEncodedValueByPath(node?.valuePath);
      if (this.isDynamicEncodedField(node?.field)) {
        return state?.value ?? "";
      }
      return state ?? "";
    },
    handleEncodedNumericInput(node, event) {
      if (!node?.field || node.field.readOnly === true) {
        return;
      }

      const rawValue = event?.target?.value ?? "";
      this.setEncodedNodeValue(node, rawValue);
      const normalizedValue = this.getEncodedNodeValue(node);
      if (event?.target) {
        event.target.value = normalizedValue === null || normalizedValue === undefined ? "" : String(normalizedValue);
      }
    },
    setEncodedNodeValue(node, rawValue) {
      if (!node?.field || node.field.readOnly === true) {
        return;
      }

      if (this.isDynamicEncodedField(node.field)) {
        const currentState = this.getEncodedValueByPath(node.valuePath) || {
          fieldType: this.getDefaultDynamicFieldType(node.field),
          value: ""
        };
        const fieldType = currentState.fieldType || this.getDefaultDynamicFieldType(node.field);
        const normalizedValue = this.normalizeDynamicFieldValue(node.field, fieldType, rawValue);
        const { parent, key } = this.getEncodedParentByPath(node.valuePath);
        if (parent === null || key === null) {
          return;
        }
        this.$set(parent, key, {
          fieldType,
          value: normalizedValue
        });
        return;
      }

      const { parent, key } = this.getEncodedParentByPath(node.valuePath);
      if (parent === null || key === null) {
        return;
      }

      let nextValue = rawValue;
      if (node.field.type === "uint") {
        const normalizedRawValue = String(rawValue ?? "").replace(/[^\d]/g, "");
        if (normalizedRawValue === "") {
          this.$set(parent, key, "");
          return;
        }
        const min = Number(node.field.minValue ?? 0);
        const max = Number(node.field.maxValue ?? 2147483647);
        const numericValue = Number(normalizedRawValue);
        nextValue = Number.isFinite(numericValue) ? Math.min(max, Math.max(min, Math.round(numericValue))) : min;
      } else if (node.field.type !== "enum") {
        nextValue = String(rawValue ?? "");
      }

      this.$set(parent, key, nextValue);
    },
    getEncodedNodeFieldType(node) {
      if (!this.isDynamicEncodedField(node?.field)) {
        return "";
      }

      const state = this.getEncodedValueByPath(node?.valuePath);
      return state?.fieldType || this.getDefaultDynamicFieldType(node.field);
    },
    setEncodedNodeFieldType(node, nextFieldType) {
      if (!node?.field || node.field.readOnly === true || !this.isDynamicEncodedField(node.field)) {
        return;
      }

      const { parent, key } = this.getEncodedParentByPath(node.valuePath);
      if (parent === null || key === null) {
        return;
      }

      this.$set(parent, key, {
        fieldType: nextFieldType,
        value: this.getDefaultDynamicFieldValue(node.field, nextFieldType)
      });
    },
    normalizeDynamicFieldValue(field, fieldType, rawValue) {
      switch (fieldType) {
        case "boolean":
          return String(rawValue) === "True" ? "True" : "False";
        case "unsignedint": {
          const normalizedRawValue = String(rawValue ?? "").replace(/[^\d]/g, "");
          if (normalizedRawValue === "") {
            return "";
          }
          const min = Number(field?.minValue ?? 0);
          const max = Number(field?.maxValue ?? 2147483647);
          const numericValue = Number(normalizedRawValue);
          const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : min;
          return String(Math.min(max, Math.max(min, safeValue)));
        }
        case "signedint": {
          const normalizedRawValue = String(rawValue ?? "").replace(/[^\d-]/g, "");
          if (normalizedRawValue === "" || normalizedRawValue === "-") {
            return "";
          }
          const min = Number(field?.minValue ?? -2147483648);
          const max = Number(field?.maxValue ?? 2147483647);
          const numericValue = Number(normalizedRawValue);
          const safeValue = Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
          return String(Math.min(max, Math.max(min, safeValue)));
        }
        case "string":
        case "data":
          return String(rawValue ?? "");
        case "enum":
        default: {
          const options = this.getEncodedFieldOptions(field);
          const optionValues = options.map((item) => String(item.value));
          const candidate = String(rawValue ?? "");
          if (optionValues.includes(candidate)) {
            return candidate;
          }
          return String(field?.defaultValue ?? options[0]?.value ?? "0");
        }
      }
    },
    shouldRenderEncodedNodeValueAsSelect(node) {
      if (this.isDynamicEncodedField(node?.field)) {
        const fieldType = this.getEncodedNodeFieldType(node);
        return fieldType === "enum" || fieldType === "boolean";
      }
      return this.shouldRenderEncodedSelect(node?.field);
    },
    getEncodedNodeValueOptions(node) {
      if (this.isDynamicEncodedField(node?.field)) {
        const fieldType = this.getEncodedNodeFieldType(node);
        if (fieldType === "boolean") {
          return [
            { value: "False", label: "False" },
            { value: "True", label: "True" }
          ];
        }
        if (fieldType === "enum") {
          return this.getEncodedFieldOptions(node.field);
        }
      }
      return this.getEncodedFieldOptions(node?.field);
    },
    getEncodedNodeValueLabel(node) {
      return node?.field?.name || "";
    },
    isEncodedNodeTextarea(node) {
      if (!this.isDynamicEncodedField(node?.field)) {
        return false;
      }
      const fieldType = this.getEncodedNodeFieldType(node);
      return fieldType === "string" || fieldType === "data";
    },
    isEncodedNodeNumeric(node) {
      if (this.isDynamicEncodedField(node?.field)) {
        const fieldType = this.getEncodedNodeFieldType(node);
        return fieldType === "unsignedint" || fieldType === "signedint";
      }
      return node?.field?.type === "uint";
    },
    getEncodedNodeMinValue(node) {
      if (this.isDynamicEncodedField(node?.field)) {
        return this.getEncodedNodeFieldType(node) === "signedint" ? -2147483648 : 0;
      }
      return node?.field?.minValue ?? 0;
    },
    getEncodedNodeMaxValue(node) {
      if (this.isDynamicEncodedField(node?.field)) {
        return 2147483647;
      }
      return node?.field?.maxValue ?? 2147483647;
    },
    getEncodedArrayCount(nodeOrField) {
      const arrayState = nodeOrField?.valuePath
        ? this.getEncodedValueByPath(nodeOrField.valuePath)
        : this.encoded.fieldValues[nodeOrField.name];
      return (arrayState?.elements || []).length;
    },
    setEncodedArrayCount(nodeOrField, rawValue) {
      const field = nodeOrField?.field || nodeOrField;
      const path = nodeOrField?.valuePath || [field.name];
      const min = Number(field.minElements ?? 0);
      const max = Number(field.maxElements ?? 255);
      let count = Number(rawValue);
      if (!Number.isFinite(count)) {
        count = min;
      }
      count = Math.min(max, Math.max(min, Math.round(count)));

      const currentState = this.getEncodedValueByPath(path) || {
        elements: []
      };

      const nextElements = Array.isArray(currentState.elements) ? [...currentState.elements] : [];
      while (nextElements.length < count) {
        nextElements.push(this.createDefaultEncodedElement(field.elementFields));
      }
      if (nextElements.length > count) {
        nextElements.length = count;
      }

      const { parent, key } = this.getEncodedParentByPath(path);
      if (parent === null || key === null) {
        return;
      }

      this.$set(parent, key, {
        elements: nextElements
      });
    },
    buildEncodedSubmitFields() {
      return this.encodedFieldDefs
        .map((field) => this.buildEncodedSubmitField(field, this.encoded.fieldValues[field.name]))
        .filter((field) => field !== null);
    },
    buildEncodedSubmitField(field, value) {
      if (field.type === "array") {
        return {
          Name: field.name,
          Value: "",
          Elements: ((value && value.elements) || []).map((element, index) => ({
            Index: index,
            Fields: (field.elementFields || [])
              .map((childField) => this.buildEncodedSubmitField(childField, element[childField.name]))
              .filter((child) => child !== null)
          }))
        };
      }

      return {
        Name: field.name,
        Value: String(this.isDynamicEncodedField(field) ? value?.value ?? "" : value ?? ""),
        Type: this.isDynamicEncodedField(field) ? String(value?.fieldType ?? this.getDefaultDynamicFieldType(field)) : ""
      };
    },
    findMissingRequiredEncodedField(fields, values, pathPrefix = "") {
      for (const field of fields || []) {
        const path = pathPrefix ? `${pathPrefix}.${field.name}` : field.name;
        const value = values?.[field.name];

        if (field.type === "array") {
          const elements = value?.elements || [];
          for (let index = 0; index < elements.length; index += 1) {
            const missing = this.findMissingRequiredEncodedField(field.elementFields || [], elements[index], `${path}[${index}]`);
            if (missing) {
              return missing;
            }
          }
          continue;
        }

        const normalizedValue = this.isDynamicEncodedField(field) ? value?.value : value;
        if (field.required && (normalizedValue === null || normalizedValue === undefined || normalizedValue === "")) {
          return {
            path,
            label: field.label || field.name
          };
        }
      }

      return null;
    },
    async fetchMemberMobiles() {
      const memberId = this.getUserMemberId();
      if (!memberId) {
        this.mobiles = [];
        this.mobileLoadError = "無法取得登入者資訊，請重新登入";
        return;
      }

      this.isLoadingMobiles = true;
      this.mobileLoadError = "";

      try {
        const response = await fetch(this.getApiBase() + GET_MEMBER_MOBILE, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            MemberId: memberId
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        if (!response.ok || (payload && payload.status === "failure")) {
          throw new Error((payload && payload.message) || `取得 Mobile 失敗 (${response.status})`);
        }

        const data = (payload && payload.data) || {};
        const list = data.mobileIdList || data.MobileIdList || [];
        this.mobiles = list
          .map((mobileId, index) => ({
            id: index + 1,
            mobileId: String(mobileId || "").trim()
          }))
          .filter((item) => item.mobileId);

        if (!this.selectedMobileId && this.mobiles.length > 0) {
          this.selectedMobileId = this.mobiles[0].mobileId;
        }
        if (!this.receiveSelectedMobileId && this.mobiles.length > 0) {
          this.receiveSelectedMobileId = this.mobiles[0].mobileId;
        }
      } catch (error) {
        this.mobiles = [];
        this.mobileLoadError = error.message || "取得 Mobile 失敗";
      } finally {
        this.isLoadingMobiles = false;
      }
    },
    toDateTimeBoundary(dateText, isEnd) {
      if (!dateText) {
        return "";
      }
      return `${dateText} ${isEnd ? "23:59:59" : "00:00:00"}`;
    },
    formatLocalDateTime(date) {
      const pad = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    formatDateTimeFromApi(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return this.formatLocalDateTime(date);
    },
    collectFieldNameValueLines(fields, result) {
      if (!Array.isArray(fields) || fields.length === 0) {
        return;
      }

      fields.forEach((field) => {
        const name = field?.name || field?.Name || "";
        const value = field?.value ?? field?.Value ?? "";
        if (name) {
          result.push(`${name} = ${value}`);
        }

        const nestedMessageFields = field?.message?.fields || field?.Message?.Fields;
        if (nestedMessageFields) {
          this.collectFieldNameValueLines(nestedMessageFields, result);
        }

        const elements = field?.elements || field?.Elements || [];
        if (Array.isArray(elements) && elements.length > 0) {
          elements.forEach((element) => {
            const elementFields = element?.fields || element?.Fields;
            if (elementFields) {
              this.collectFieldNameValueLines(elementFields, result);
            }
          });
        }
      });
    },
    buildMessengerDecodeLines(fields) {
      const lines = [];
      this.collectFieldNameValueLines(fields, lines);
      return lines;
    },
    hasMessengerLatLong(lines) {
      if (!Array.isArray(lines) || lines.length === 0) {
        return false;
      }
      const lowerLines = lines.map((line) => String(line || "").toLowerCase());
      const hasLatitude = lowerLines.some((line) => line.startsWith("latitude"));
      const hasLongitude = lowerLines.some((line) => line.startsWith("longitude"));
      return hasLatitude && hasLongitude;
    },
    formatCoordinate(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return String(value);
    },
    getSinDisplay(sin) {
      const sinDef = (this.encodedDefinitions || []).find((item) => String(item.sin) === String(sin));
      return sinDef?.sinName || String(sin);
    },
    flattenPayloadFieldEntries(fields, result = []) {
      if (!Array.isArray(fields) || fields.length === 0) {
        return result;
      }

      fields.forEach((field) => {
        const name = field?.name || field?.Name || "";
        const value = field?.value ?? field?.Value ?? "";
        if (name) {
          result.push({ name: String(name), value: String(value) });
        }

        const nestedMessageFields = field?.message?.fields || field?.Message?.Fields;
        if (nestedMessageFields) {
          this.flattenPayloadFieldEntries(nestedMessageFields, result);
        }

        const elements = field?.elements || field?.Elements || [];
        if (Array.isArray(elements) && elements.length > 0) {
          elements.forEach((element) => {
            const elementFields = element?.fields || element?.Fields;
            if (elementFields) {
              this.flattenPayloadFieldEntries(elementFields, result);
            }
          });
        }
      });

      return result;
    },
    doesMinDefinitionMatchPayload(minDef, payloadFields) {
      const fieldDefs = minDef?.fields || [];
      const flattenedPayloadFields = this.flattenPayloadFieldEntries(payloadFields);
      if (fieldDefs.length === 0 || flattenedPayloadFields.length === 0) {
        return false;
      }

      let comparedFieldCount = 0;
      for (const fieldDef of fieldDefs) {
        if (!fieldDef?.name || fieldDef.type === "array") {
          continue;
        }

        const payloadField = flattenedPayloadFields.find((item) => item.name === String(fieldDef.name));
        if (!payloadField) {
          continue;
        }

        comparedFieldCount += 1;
        const expectedValue = fieldDef.defaultValue ?? "";
        if (String(payloadField.value) !== String(expectedValue)) {
          return false;
        }
      }

      return comparedFieldCount > 0;
    },
    getMinDisplay(sin, min, payloadFields = []) {
      const sinDef = (this.encodedDefinitions || []).find((item) => String(item.sin) === String(sin));
      const minDefs = (sinDef?.mins || []).filter((item) => String(item.min) === String(min));
      if (minDefs.length === 0) {
        return String(min);
      }

      const matchedMinDef = minDefs.find((item) => this.doesMinDefinitionMatchPayload(item, payloadFields));
      const minDef = matchedMinDef || minDefs[0];
      return minDef?.minName || String(min);
    },
    mapReceiveItem(item) {
      const messageSize = item.otaMessageSize ?? item.OTAMessageSize;
      const dataLength = item.dataLength ?? item.DataLength ?? 0;
      const fields = item.fields || item.Fields || [];
      const messengerDecodeLines = this.buildMessengerDecodeLines(fields);
      return {
        returnMessageId: item.returnMessageId ?? item.ReturnMessageId ?? "",
        messageId: item.returnMessageId ?? item.ReturnMessageId ?? "",
        sin: item.sin ?? item.Sin ?? "",
        min: item.min ?? item.Min ?? "",
        from: item.mobileId || item.MobileId || "",
        received: this.formatDateTimeFromApi(item.messageLocal || item.MessageLocal || item.messageUtc || item.MessageUtc),
        transport: item.transportDesc || item.TransportDesc || "",
        region: item.regionName || item.RegionName || "",
        messageLength: `${messageSize ?? 0} bytes`,
        dataLength: `${dataLength} bytes`,
        dateHex: item.dataHex || item.DataHex || "",
        dataAscii: item.dataAscii || item.DataAscii || "",
        messengerDecodeLines,
        hasLatLongDecode: this.hasMessengerLatLong(messengerDecodeLines),
        latitudeDisplay: this.formatCoordinate(item.latitude ?? item.Latitude),
        longitudeDisplay: this.formatCoordinate(item.longitude ?? item.Longitude)
      };
    },
    mapSendItem(item) {
      const payload = item.payload || item.Payload || {};
      const fields = payload.fields || payload.Fields || [];
      const messengerDecodeLines = this.buildMessengerDecodeLines(fields);
      const messageLength = Number(item.messageLength ?? item.MessageLength ?? 0);
      const dataLength = Number(item.dataLength ?? item.DataLength ?? 0);
      const transportDesc = item.transportDesc || item.TransportDesc || "";
      const stateDesc = item.stateDesc || item.StateDesc || "";
      const messageId = item.forwardMessageID ?? item.ForwardMessageID ?? "";

      return {
        messageId: String(messageId),
        sin: item.sin ?? item.Sin ?? 0,
        min: item.min ?? item.Min ?? 0,
        to: item.destinationID || item.DestinationID || "",
        sent: this.formatDateTimeFromApi(item.createLocal || item.CreateLocal || item.createUTC || item.CreateUTC),
        state: stateDesc,
        acknowledged: this.formatDateTimeFromApi(item.stateLocal || item.StateLocal || item.stateUTC || item.StateUTC),
        transport: transportDesc,
        messageLength: `${messageLength} bytes`,
        dataLength: `${dataLength} bytes`,
        dataHex: item.dataHex || item.DataHex || "",
        dataAscii: item.dataAscii || item.DataAscii || "",
        payloadFields: fields,
        messengerDecodeLines,
        hasLatLongDecode: this.hasMessengerLatLong(messengerDecodeLines)
      };
    },
    async fetchReceiveListByPage(isNewQuery) {
      if (!this.receiveSelectedMobileId) {
        this.receiveErrorMessage = "請選擇 Mobile";
        this.receiveList = [];
        return;
      }
      if (!this.receiveStartDate || !this.receiveEndDate) {
        this.receiveErrorMessage = "請選擇起始日期與結束日期";
        this.receiveList = [];
        return;
      }

      const startDate = this.toDateTimeBoundary(this.receiveStartDate, false);
      const endDate = this.toDateTimeBoundary(this.receiveEndDate, true);

      if (new Date(startDate).getTime() > new Date(endDate).getTime()) {
        this.receiveErrorMessage = "起始日期不可大於結束日期";
        this.receiveList = [];
        return;
      }

      if (isNewQuery || !this.receiveSnapshot) {
        this.receiveSnapshot = this.formatLocalDateTime(new Date());
      }

      this.isLoadingReceive = true;
      this.receiveErrorMessage = "";

      try {
        const response = await fetch(this.getApiBase() + GET_RETURN_MESSAGE_BY_PAGE, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            StartDate: startDate,
            EndDate: endDate,
            MobileId: this.receiveSelectedMobileId,
            Snapshot: this.receiveSnapshot,
            PageIndex: this.receiveCurrentPage,
            PageSize: this.receivePageSize
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        const statusText = String(payload?.status ?? payload?.Status ?? "").toLowerCase();
        if (!response.ok || statusText === "failure") {
          throw new Error((payload && payload.message) || `取得接收資料失敗 (${response.status})`);
        }

        const data = payload?.data || payload?.Data || {};
        const list = data.returnMessageList || data.ReturnMessageList || [];
        this.receiveList = list.map(this.mapReceiveItem);
        this.receiveTotalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
        this.receiveTotalPagesFromApi = Number(data.totalPages ?? data.TotalPages ?? 0);

        if (data.snapshot || data.Snapshot) {
          this.receiveSnapshot = data.snapshot || data.Snapshot;
        }

        if (this.receiveCurrentPage > this.receiveTotalPages) {
          this.receiveCurrentPage = this.receiveTotalPages;
        }
      } catch (error) {
        this.receiveList = [];
        this.receiveTotalCount = 0;
        this.receiveTotalPagesFromApi = 0;
        this.receiveErrorMessage = error.message || "取得接收資料失敗";
      } finally {
        this.isLoadingReceive = false;
      }
    },
    async fetchSendListByPage(isNewQuery) {
      if (!this.selectedMobileId) {
        this.sendErrorMessage = "請選擇 Mobile";
        this.sendList = [];
        return;
      }
      if (!this.sendStartDate || !this.sendEndDate) {
        this.sendErrorMessage = "請選擇起始日期與結束日期";
        this.sendList = [];
        return;
      }

      const startDate = this.toDateTimeBoundary(this.sendStartDate, false);
      const endDate = this.toDateTimeBoundary(this.sendEndDate, true);

      if (new Date(startDate).getTime() > new Date(endDate).getTime()) {
        this.sendErrorMessage = "起始日期不可大於結束日期";
        this.sendList = [];
        return;
      }

      if (isNewQuery || !this.sendSnapshot) {
        this.sendSnapshot = this.formatLocalDateTime(new Date());
      }

      this.isLoadingSend = true;
      this.sendErrorMessage = "";

      try {
        const response = await fetch(this.getApiBase() + GET_FORWARD_STATUS_BY_PAGE, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            StartDate: startDate,
            EndDate: endDate,
            MobileId: this.selectedMobileId,
            Snapshot: this.sendSnapshot,
            PageIndex: this.sendCurrentPage,
            PageSize: this.sendPageSize
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        const statusText = String(payload?.status ?? payload?.Status ?? "").toLowerCase();
        if (!response.ok || statusText === "failure") {
          throw new Error((payload && payload.message) || `取得傳送資料失敗 (${response.status})`);
        }

        const data = payload?.data || payload?.Data || {};
        const list = data.forwardStatusList || data.ForwardStatusList || [];
        this.sendList = list.map(this.mapSendItem);
        this.sendTotalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
        this.sendTotalPagesFromApi = Number(data.totalPages ?? data.TotalPages ?? 0);

        if (data.snapshot || data.Snapshot) {
          this.sendSnapshot = data.snapshot || data.Snapshot;
        }

        if (this.sendCurrentPage > this.sendTotalPages) {
          this.sendCurrentPage = this.sendTotalPages;
        }
      } catch (error) {
        this.sendList = [];
        this.sendTotalCount = 0;
        this.sendTotalPagesFromApi = 0;
        this.sendErrorMessage = error.message || "取得傳送資料失敗";
      } finally {
        this.isLoadingSend = false;
      }
    },
    connect() {
      if (!this.selectedMobileId) {
        return;
      }
      this.isConnected = true;
    },
    disconnect() {
      this.isConnected = false;
    },
    async queryReceive() {
      this.receiveCurrentPage = 1;
      await this.fetchReceiveListByPage(true);
    },
    async querySend() {
      this.sendCurrentPage = 1;
      await this.fetchSendListByPage(true);
    },
    resetPolling() {
      this.stopPolling();
    },
    startPolling() {
      // no-op
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    openDetail(item) {
      this.detailItem = item;
      this.showDetail = true;
    },
    closeDetail() {
      this.showDetail = false;
      this.detailItem = {};
    },
    openSendDetail(item) {
      this.sendDetailItem = item;
      this.showSendDetail = true;
    },
    closeSendDetail() {
      this.showSendDetail = false;
      this.sendDetailItem = {};
    },
    async sendEncoded() {
      if (!this.selectedMobileId) {
        const message = "請選擇 Mobile";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      const sin = Number(this.encoded.sin);
      const min = Number(this.encoded.min);
      if (!Number.isInteger(sin) || sin < 0 || sin > 255) {
        const message = "請選擇有效的 SIN";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }
      if (!Number.isInteger(min) || min < 0 || min > 255) {
        const message = "請選擇有效的 MIN";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      const missingRequiredField = this.findMissingRequiredEncodedField(this.encodedFieldDefs, this.encoded.fieldValues);

      if (missingRequiredField) {
        const message = `請輸入欄位 ${missingRequiredField.path || missingRequiredField.label}`;
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      try {
        const response = await fetch(this.getApiBase() + SUBMIT_ENCODED_MESSAGES, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            MobileId: this.selectedMobileId,
            Sin: sin,
            Min: min,
            Fields: this.buildEncodedSubmitFields()
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        const statusText = String(payload?.status ?? payload?.Status ?? "").toLowerCase();
        if (!response.ok || statusText === "failure") {
          throw new Error((payload && payload.message) || `Encoded 傳送失敗 (${response.status})`);
        }

        this.sendErrorMessage = "";
        this.$toast.success((payload && payload.message) || "Encoded 傳送成功!");
        await this.querySend();
      } catch (error) {
        const message = error.message || "Encoded 傳送失敗";
        this.sendErrorMessage = message;
        this.$toast.error(message);
      }
    },
    getRawAsciiText() {
      if (this.raw.mode === "ascii") {
        return String(this.raw.data ?? "");
      }
      return this.hexToAscii(this.raw.data);
    },
    saveRawAsciiToTxt() {
      const asciiText = this.getRawAsciiText();
      const blob = new Blob([asciiText], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "raw-ascii.txt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    triggerLoadAsciiTxt() {
      const input = this.$refs.rawAsciiFileInput;
      if (!input) {
        return;
      }
      input.click();
    },
    handleLoadAsciiTxt(event) {
      const input = event && event.target;
      const file = input && input.files && input.files[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        this.raw.mode = "ascii";
        this.raw.data = String(reader.result ?? "");
        if (input) {
          input.value = "";
        }
      };
      reader.onerror = () => {
        if (input) {
          input.value = "";
        }
      };
      reader.readAsText(file, "utf-8");
    },
    switchRawMode(nextMode) {
      if (nextMode !== "hex" && nextMode !== "ascii") {
        return;
      }
      if (this.raw.mode === nextMode) {
        return;
      }

      if (nextMode === "hex") {
        this.raw.data = this.asciiToHex(this.raw.data);
      } else {
        this.raw.data = this.hexToAscii(this.raw.data);
      }
      this.raw.mode = nextMode;
    },
    asciiToHex(asciiText) {
      const text = String(asciiText ?? "");
      const bytes = [];
      for (let index = 0; index < text.length; index += 1) {
        bytes.push(text.charCodeAt(index).toString(16).padStart(2, "0"));
      }
      return bytes.join(" ");
    },
    hexToAscii(hexText) {
      const normalized = String(hexText ?? "").replace(/\s+/g, "");
      if (!normalized) {
        return "";
      }

      const result = [];
      for (let index = 0; index < normalized.length; index += 2) {
        const hexPair = normalized.slice(index, index + 2);
        if (hexPair.length < 2 || !/^[\da-fA-F]{2}$/.test(hexPair)) {
          continue;
        }
        result.push(String.fromCharCode(parseInt(hexPair, 16)));
      }
      return result.join("");
    },
    clampRawHeaderValue(fieldName) {
      if (fieldName !== "sin" && fieldName !== "min") {
        return;
      }

      const normalizedValue = String(this.raw[fieldName] ?? "").replace(/[^\d]/g, "");
      if (!normalizedValue) {
        this.raw[fieldName] = "";
        return;
      }

      const currentValue = Number(normalizedValue);
      if (!Number.isFinite(currentValue)) {
        this.raw[fieldName] = "";
        return;
      }

      const rounded = Math.round(currentValue);
      this.raw[fieldName] = Math.min(255, Math.max(0, rounded));
    },
    async sendRaw() {
      if (!this.selectedMobileId) {
        const message = "請選擇 Mobile";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      if (this.raw.sin === "" || this.raw.sin === null || this.raw.sin === undefined) {
        const message = "請輸入 SIN";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }
      if (this.raw.min === "" || this.raw.min === null || this.raw.min === undefined) {
        const message = "請輸入 MIN";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      const sin = Number(this.raw.sin);
      const min = Number(this.raw.min);
      if (!Number.isInteger(sin) || sin < 0 || sin > 255) {
        const message = "SIN 必須介於 0 到 255";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }
      if (!Number.isInteger(min) || min < 0 || min > 255) {
        const message = "MIN 必須介於 0 到 255";
        this.sendErrorMessage = message;
        this.$toast.error(message);
        return;
      }

      const rawPayloadAscii = this.getRawAsciiText();

      try {
        const response = await fetch(this.getApiBase() + SUBMIT_RAW_MESSAGES, {
          method: "POST",
          headers: this.buildHeaders(),
          body: JSON.stringify({
            MobileId: this.selectedMobileId,
            Sin: sin,
            Min: min,
            RawPayload: rawPayloadAscii
          })
        });

        let payload = null;
        try {
          payload = await response.json();
        } catch (error) {
          payload = null;
        }

        const statusText = String(payload?.status ?? payload?.Status ?? "").toLowerCase();
        if (!response.ok || statusText === "failure") {
          throw new Error((payload && payload.message) || `傳送失敗 (${response.status})`);
        }

        this.sendErrorMessage = "";
        this.$toast.success((payload && payload.message) || "資料傳送成功!");
        await this.querySend();
      } catch (error) {
        const message = error.message || "傳送失敗";
        this.sendErrorMessage = message;
        this.$toast.error(message);
      }
    },
    async prevReceivePage() {
      if (this.canPrevReceive) {
        this.receiveCurrentPage -= 1;
        await this.fetchReceiveListByPage(false);
      }
    },
    async nextReceivePage() {
      if (this.canNextReceive) {
        this.receiveCurrentPage += 1;
        await this.fetchReceiveListByPage(false);
      }
    },
    async prevSendPage() {
      if (this.canPrevSend) {
        this.sendCurrentPage -= 1;
        await this.fetchSendListByPage(false);
      }
    },
    async nextSendPage() {
      if (this.canNextSend) {
        this.sendCurrentPage += 1;
        await this.fetchSendListByPage(false);
      }
    },
    ensureReceivePageInRange() {
      if (this.receiveCurrentPage > this.receiveTotalPages) {
        this.receiveCurrentPage = this.receiveTotalPages;
      }
      if (this.receiveCurrentPage < 1) {
        this.receiveCurrentPage = 1;
      }
    },
    ensureSendPageInRange() {
      if (this.sendCurrentPage > this.sendTotalPages) {
        this.sendCurrentPage = this.sendTotalPages;
      }
      if (this.sendCurrentPage < 1) {
        this.sendCurrentPage = 1;
      }
    },
    formatDateTime(date) {
      return date.toISOString().slice(0, 19).replace("T", " ");
    },
  }
};
</script>

<style scoped>
.page {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 12px 24px rgba(15, 30, 40, 0.08);
  font-size: 1rem;
}

.control-card {
  display: grid;
  grid-template-columns: minmax(180px, 220px) minmax(180px, 220px) auto;
  gap: 16px;
  align-items: end;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(15, 30, 40, 0.08);
  background: #f9fbfb;
  margin-bottom: 20px;
}

.control-card-section {
  grid-column: 1 / -1;
}

.receive-filter {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 220px) minmax(180px, 220px) auto;
  gap: 12px;
  align-items: end;
  margin-bottom: 16px;
}

.control-actions {
  display: flex;
  gap: 10px;
}

.status {
  font-size: 0.9375rem;
  color: rgba(31, 42, 51, 0.7);
}

.status.connected {
  color: #0f8a5f;
  font-weight: 600;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9375rem;
}

.form-field select {
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 1rem;
}

.form-field input {
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 12px;
  font-size: 1rem;
}

.query-btn {
  align-self: end;
  justify-self: start;
}

.btn {
  min-width: 90px;
  min-height: 40px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
}

.btn.primary {
  background: #0aa0a5;
  color: #ffffff;
}

.btn.ghost {
  background: #eef2f4;
  color: #1f2a33;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.tab-card {
  border-radius: 14px;
  border: 1px solid rgba(15, 30, 40, 0.08);
  overflow: hidden;
}

.tabs {
  display: flex;
  background: #f4f7f8;
}

.tab {
  flex: 1;
  padding: 14px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
}

.tab.active {
  background: #ffffff;
  font-weight: 600;
  border-bottom: 2px solid #0aa0a5;
}

.tab-body {
  padding: 16px;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 30, 40, 0.08);
}

.table-card table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
}

.table-card thead {
  background: #f4f7f8;
  text-align: left;
}

.table-card th,
.table-card td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(15, 30, 40, 0.08);
}

.clickable-row {
  cursor: pointer;
}

.table-card tbody tr:hover {
  background: rgba(10, 160, 165, 0.06);
}

.empty {
  text-align: center;
  padding: 24px 0;
  color: rgba(31, 42, 51, 0.5);
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}

.page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
}

.page-size select {
  width: 80px;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 0 10px;
  font-size: 0.9375rem;
}

.pager {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
}

.pager button {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  background: #ffffff;
  cursor: pointer;
  font-size: 0.9375rem;
}

.pager button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.placeholder {
  padding: 24px;
  border-radius: 12px;
  background: #f9fbfb;
  color: rgba(31, 42, 51, 0.7);
}

.send-mobile-picker {
  max-width: 320px;
  margin-bottom: 12px;
}

.send-compose-card {
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(15, 30, 40, 0.08);
  background: #f9fbfb;
  margin-bottom: 20px;
}

.send-tabs {
  display: inline-flex;
  gap: 6px;
  margin-bottom: 16px;
  background: #eef3f4;
  padding: 6px;
  border-radius: 999px;
}

.send-tab {
  border: none;
  background: transparent;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 1rem;
}

.send-tab.active {
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(15, 30, 40, 0.08);
  font-weight: 600;
}

.send-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.encoded-layout {
  display: grid;
  grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
  gap: 16px;
}

.encoded-tree,
.encoded-editor {
  border: 1px solid rgba(20, 40, 50, 0.12);
  border-radius: 12px;
  background: #ffffff;
  padding: 14px;
}

.encoded-tree-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 10px;
}

.encoded-field-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid rgba(20, 40, 50, 0.12);
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  margin-bottom: 8px;
  font-size: 1rem;
  line-height: 1.45;
}

.encoded-field-item.active {
  border-color: #0aa0a5;
  background: rgba(10, 160, 165, 0.08);
}

.encoded-field-item span {
  font-size: 1rem;
  font-weight: 600;
}

.encoded-field-item small {
  font-size: 0.9375rem;
  color: rgba(31, 42, 51, 0.65);
}

.encoded-editor input[readonly],
.encoded-editor textarea[readonly],
.encoded-editor select:disabled {
  background: #f1f5f6;
  color: rgba(31, 42, 51, 0.78);
  border-color: rgba(20, 40, 50, 0.14);
  cursor: not-allowed;
}

.encoded-editor input[readonly]:focus,
.encoded-editor textarea[readonly]:focus,
.encoded-editor select:disabled:focus {
  box-shadow: none;
  border-color: rgba(20, 40, 50, 0.14);
}

.description-box {
  min-height: 110px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  border-radius: 10px;
  background: #f9fbfb;
  padding: 10px 12px;
  white-space: pre-wrap;
  line-height: 1.5;
}

.inline-error {
  color: #b91c1c;
  font-size: 0.9375rem;
}

.placeholder.small {
  padding: 12px;
}

.send-history {
  margin-top: 18px;
}

.raw-toolbar {
  display: flex;
  gap: 10px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-field textarea {
  border-radius: 10px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  padding: 10px 12px;
  font-size: 1rem;
  resize: vertical;
}

.form-field textarea:focus {
  outline: none;
  border-color: rgba(10, 160, 165, 0.7);
  box-shadow: 0 0 0 3px rgba(10, 160, 165, 0.15);
}

.send-actions {
  display: flex;
  justify-content: flex-end;
}

.data-box {
  border-radius: 12px;
  border: 1px solid rgba(20, 40, 50, 0.2);
  overflow: hidden;
  background: #ffffff;
}

.data-tabs {
  display: flex;
  gap: 6px;
  padding: 6px;
  background: #f4f7f8;
}

.data-tab {
  border: none;
  background: transparent;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9375rem;
}

.data-tab.active {
  background: #ffffff;
  box-shadow: 0 4px 8px rgba(15, 30, 40, 0.08);
  font-weight: 600;
}

.data-box textarea {
  border: none;
  border-radius: 0;
  width: 100%;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 16, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.modal {
  width: min(920px, 94vw);
  max-height: min(88vh, 900px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(15, 30, 40, 0.2);
  padding: 20px 22px 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.375rem;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
}

.detail-item span {
  font-weight: 600;
  margin-right: 6px;
}

.detail-item {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.detail-item-left {
  grid-column: 1;
}
.detail-item-full {
  grid-column: 1 / -1;
  margin-top: 12px;
}

.messenger-decode {
  margin-top: 6px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

@media (max-width: 960px) {
  .control-card {
    grid-template-columns: 1fr;
  }

  .receive-filter {
    grid-template-columns: 1fr;
  }

  .control-actions {
    justify-content: flex-start;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .encoded-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .page {
    padding: 20px;
  }

  .table-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .pager {
    justify-content: space-between;
  }
}
</style>
